import { config } from '../../config';
import { fetchWithAuth } from '../services/fetchWithAuth';

const jsonHeaders = { 'Content-Type': 'application/json' };

const base = () => config().ingestionServiceBaseUrl;

const qs = (params = {}) => {
    const entries = Object.entries(params).filter(([, v]) => v != null && v !== '');
    return entries.length ? `?${new URLSearchParams(entries).toString()}` : '';
};

async function parse(res) {
    if (!res.ok) {
        let detail = '';
        try {
            detail = await res.text();
        } catch {
            detail = '';
        }
        try {
            const { message } = JSON.parse(detail);
            detail = [].concat(message || []).join('; ') || detail;
        } catch {
            // not a Nest JSON error body — show it as-is
        }
        throw new Error(detail || res.statusText || `HTTP ${res.status}`);
    }
    const contentType = res.headers.get('Content-Type') || '';
    return contentType.includes('application/json') ? res.json() : {};
}

export const ingestionApi = {
    listItems(params = {}) {
        return fetchWithAuth(`${base()}/pipeline-items${qs(params)}`).then(parse);
    },

    getCreators() {
        return fetchWithAuth(`${base()}/pipeline-items/creators`).then(parse);
    },

    getReport(params = {}) {
        return fetchWithAuth(`${base()}/pipeline-items/report${qs(params)}`).then(parse);
    },

    getItem(id) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}`).then(parse);
    },

    getCosts(params = {}) {
        return fetchWithAuth(`${base()}/usage/costs${qs(params)}`).then(parse);
    },

    getItemCosts(id) {
        return fetchWithAuth(`${base()}/usage/costs/items/${id}`).then(parse);
    },

    createItem(payload) {
        return fetchWithAuth(`${base()}/pipeline-items`, {
            method: 'POST',
            headers: jsonHeaders,
            body: JSON.stringify(payload),
        }).then(parse);
    },

    deleteItem(id) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}`, { method: 'DELETE' }).then(parse);
    },

    updateVariations(id, payload) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/variations`, {
            method: 'PUT',
            headers: jsonHeaders,
            body: JSON.stringify(payload),
        }).then(parse);
    },

    runStep(id, step) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/steps/${step}/run`, {
            method: 'POST',
        }).then(parse);
    },

    /** Admin rejection of a user request; `reason` is shown to the submitter. */
    rejectItem(id, reason) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/reject`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ reason }),
        }).then(parse);
    },

    approveDuplicates(id) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/approve-duplicates`, {
            method: 'POST',
        }).then(parse);
    },

    getScrapeSummary(id) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/scrape`).then(parse);
    },

    getVariantRaw(id, variantId) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/variants/${variantId}/scraped`).then(parse);
    },

    updateExtracted(id, variantId, patch) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/variants/${variantId}/extracted`, {
            method: 'PUT',
            headers: jsonHeaders,
            body: JSON.stringify(patch),
        }).then(parse);
    },

    getVariationImages(id, variationId) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/variations/${variationId}/images`).then(parse);
    },

    getVariantImages(id, variantId) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/variants/${variantId}/images`).then(parse);
    },

    getItemImages(id) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/images`).then(parse);
    },

    setVariantImageFromUrl(id, variantId, sourceUrl) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/variants/${variantId}/image-from-url`, {
            method: 'POST',
            headers: jsonHeaders,
            body: JSON.stringify({ sourceUrl }),
        }).then(parse);
    },

    setVariationThumbnail(id, variationId, sourceUrl) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/variations/${variationId}/thumbnail`, {
            method: 'POST',
            headers: jsonHeaders,
            body: JSON.stringify({ sourceUrl }),
        }).then(parse);
    },

    uploadVariationImage(id, variationId, file) {
        const form = new FormData();
        form.append('file', file);
        return fetchWithAuth(`${base()}/pipeline-items/${id}/variations/${variationId}/image`, {
            method: 'POST',
            body: form,
        }).then(parse);
    },

    removeVariationBackground(id, variationId) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/variations/${variationId}/thumbnail/remove-background`, {
            method: 'POST',
        }).then(parse);
    },

    extractVariationWallPrint(id, variationId) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/variations/${variationId}/thumbnail/extract-wall-print`, {
            method: 'POST',
        }).then(parse);
    },

    generateVariationTexture(id, variationId) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/variations/${variationId}/thumbnail/generate-texture`, {
            method: 'POST',
        }).then(parse);
    },

    cropVariationImage(id, variationId, rect) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/variations/${variationId}/thumbnail/crop`, {
            method: 'POST',
            headers: jsonHeaders,
            body: JSON.stringify(rect),
        }).then(parse);
    },

    replaceVariantCutout(id, variantId, file) {
        const form = new FormData();
        form.append('file', file);
        return fetchWithAuth(`${base()}/pipeline-items/${id}/variants/${variantId}/cutout`, {
            method: 'POST',
            body: form,
        }).then(parse);
    },

    getAssets(id) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/assets`).then(parse);
    },

    async getAssetBlob(id, assetId) {
        const res = await fetchWithAuth(`${base()}/pipeline-items/${id}/assets/${assetId}/file`);
        if (!res.ok) throw new Error(`Failed to load asset (${res.status})`);
        return res.blob();
    },

    /**
     * Fast path: presigned direct-to-S3 PUT (the multi-MB file never proxies
     * through the service), then a small `complete` call records it. Falls back
     * to the legacy multipart upload when the server can't presign (local dev).
     */
    async uploadAsset(id, variantId, file) {
        const contentType = file.type || 'application/octet-stream';
        let presign = null;
        try {
            presign = await fetchWithAuth(`${base()}/pipeline-items/${id}/variants/${variantId}/assets/presign`, {
                method: 'POST',
                headers: jsonHeaders,
                body: JSON.stringify({ filename: file.name, contentType }),
            }).then(parse);
        } catch {
            presign = null; // older server / presign unsupported — use the fallback
        }
        if (presign?.url) {
            // Plain fetch: the URL is self-authorizing, and the Content-Type must
            // match the one that was signed.
            const put = await fetch(presign.url, {
                method: 'PUT',
                headers: { 'Content-Type': contentType },
                body: file,
            });
            if (!put.ok) throw new Error(`Upload to storage failed (${put.status})`);
            return fetchWithAuth(`${base()}/pipeline-items/${id}/variants/${variantId}/assets/complete`, {
                method: 'POST',
                headers: jsonHeaders,
                body: JSON.stringify({ key: presign.key }),
            }).then(parse);
        }
        const form = new FormData();
        form.append('file', file);
        // No Content-Type header — the browser sets the multipart boundary.
        return fetchWithAuth(`${base()}/pipeline-items/${id}/variants/${variantId}/assets`, {
            method: 'POST',
            body: form,
        }).then(parse);
    },

    uploadRetextureStyleImage(id, variantId, file) {
        const form = new FormData();
        form.append('file', file);
        return fetchWithAuth(`${base()}/pipeline-items/${id}/variants/${variantId}/retexture-style-image`, {
            method: 'POST',
            body: form,
        }).then(parse);
    },

    useAsAsset(id, variantId, source) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/variants/${variantId}/use-as-asset`, {
            method: 'POST',
            headers: jsonHeaders,
            body: JSON.stringify(source),
        }).then(parse);
    },

    setModelConfig(id, config) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/model-config`, {
            method: 'POST',
            headers: jsonHeaders,
            body: JSON.stringify(config || {}),
        }).then(parse);
    },

    uploadBuilderFeedbackImage(id, variantId, file) {
        const form = new FormData();
        form.append('file', file);
        return fetchWithAuth(`${base()}/pipeline-items/${id}/variants/${variantId}/builder-feedback-image`, {
            method: 'POST',
            body: form,
        }).then(parse);
    },

    regenerateVariantAsset(id, variantId, config) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/variants/${variantId}/regenerate`, {
            method: 'POST',
            headers: jsonHeaders,
            body: JSON.stringify(config || {}),
        }).then(parse);
    },

    retextureVariantAsset(id, variantId, config) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/variants/${variantId}/retexture`, {
            method: 'POST',
            headers: jsonHeaders,
            body: JSON.stringify(config || {}),
        }).then(parse);
    },

    textureRemaining(id) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/texture-remaining`, { method: 'POST' }).then(parse);
    },

    async getBuilderRenderBlob(id, variantId, view, runId) {
        const query = runId ? `?run=${encodeURIComponent(runId)}` : '';
        const res = await fetchWithAuth(`${base()}/pipeline-items/${id}/variants/${variantId}/builder-renders/${view}${query}`);
        if (!res.ok) throw new Error(`Failed to load render (${res.status})`);
        return res.blob();
    },

    setModelEngine(id, modelEngine) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/model-engine`, {
            method: 'POST',
            headers: jsonHeaders,
            body: JSON.stringify({ modelEngine }),
        }).then(parse);
    },

    setRugClass(id, rugClass) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/rug-class`, {
            method: 'POST',
            headers: jsonHeaders,
            body: JSON.stringify({ rugClass }),
        }).then(parse);
    },

    getCompression(id) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/compression`).then(parse);
    },

    publish(id) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/publish`, { method: 'POST' }).then(parse);
    },

    makeVisible(id) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/make-visible`, { method: 'POST' }).then(parse);
    },

    hide(id) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/hide`, { method: 'POST' }).then(parse);
    },

    sendBack(id, step) {
        return fetchWithAuth(`${base()}/pipeline-items/${id}/send-back/${step}`, { method: 'POST' }).then(parse);
    },
};
